import { useEffect, useState } from "react";
import { Mail, MailOpen, Trash2, User, Users } from "lucide-react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AlertMessage from "../../../components/AlertMessage";
import ModalGlobal from "../../../components/AppModal/ModalGlobal";
import { deleteNotification, getNotifications, markAllNotificationsAsRead, markNotificationAsRead } from "../api/notificationAPI";
import { formatTimestampToDDMMYYYYHHMM } from "../../../utils/formatters/DateFormatter";
import { NotificationsItemList } from "../types";
import { syncNotifications, useUser } from "../../../utils/auth";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { useAlert } from "../../../hooks/useAlert";

interface NotificationsUserListProps {
    messageName?: string;
    messageKey?: number;
    dropdownId?: string;
    refreshKey?: number;
    onLoadingChange?: (loading: boolean) => void;
    onDeleteModalOpenChange?: (isOpen: boolean) => void;
}

export default function NotificationsUserList({ messageName, messageKey, dropdownId, refreshKey, onLoadingChange, onDeleteModalOpenChange }: NotificationsUserListProps) {
    const { t } = useTranslation(["notification", "common"]);
    const [ notifications, setNotifications ] = useState<NotificationsItemList[]>([])
    const [ isNotificationsLoading, setIsNotificationsLoading ] = useState<boolean>(false)
    const [ onlyPersonals, setOnlyPersonals ] = useState<boolean>(false)
    const [ error, setError ] = useState<ApiErrorResponse>()
    const userInfo = useUser();

    const getNotificationsList = () => {
        setIsNotificationsLoading(true);
        getNotifications(onlyPersonals)
            .then(data => setNotifications(data))
            .catch((error: ApiErrorResponse) => setError(error))
            .finally(() => setIsNotificationsLoading(false));
    }

    const { showAlert } = useAlert({ fromLocation: false });
    const handleIsReadToggle = async (notification: NotificationsItemList) => {
        onLoadingChange?.(true);
        try {
            await markNotificationAsRead(notification.id);
            getNotificationsList();
            await syncNotifications((hasNotifications) => {
                userInfo.setUserInfo({
                    ...userInfo.userInfo,
                    hasNotifications,
                });
            });
        } finally {
            onLoadingChange?.(false);
        }
    }
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<NotificationsItemList | null>(null);
    const [isMarkAsReadLoading, setIsMarkAsReadLoading] = useState(false);
    const [showMarkAllAsReadButtons, setShowMarkAllAsReadButtons] = useState(false);

    const openDeleteModal = (notification: NotificationsItemList) => {
        setSelectedNotification(notification);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        if (deleteLoading) return;
        setShowDeleteModal(false);
        setSelectedNotification(null);
    };

    const handleDeleteNotification = async () => {
        if (!selectedNotification) return;

        onLoadingChange?.(true);
        setDeleteLoading(true);
        try {
            await deleteNotification(selectedNotification.id);
            setShowDeleteModal(false);
            setSelectedNotification(null);
            getNotificationsList();
            await syncNotifications((hasNotifications) => {
                userInfo.setUserInfo({
                    ...userInfo.userInfo,
                    hasNotifications,
                });
            });
        } catch (error: any) {
            const apiError = error as ApiErrorResponse;
            showAlert({
                message: apiError?.message ? t(apiError.message) : t("common:requestError.errorBadRequest"),
                type: "danger",
                duration: 8000,
            });

        } finally {
            setDeleteLoading(false);
            onLoadingChange?.(false);
        }
    };

    const markAllAsRead = async (onlyPersonal: boolean) => {
        setShowMarkAllAsReadButtons(false);
        setIsMarkAsReadLoading(true);
        markAllNotificationsAsRead(onlyPersonal)
            .then(() => {
                getNotificationsList();
            })
            .catch((error: ApiErrorResponse) => {
                showAlert({
                    message: error.message ? t(error.message) : t("common:requestError.errorBadRequest"),
                    type: 'danger',
                    duration: 8000
                });
            })
            .finally(() =>  setIsMarkAsReadLoading(false));
    }

    useEffect(() => {
        getNotificationsList();
    }, [onlyPersonals])

    useEffect(() => {
        if (refreshKey === undefined) return;
        getNotificationsList();
        syncNotifications((hasNotifications) => {
            userInfo.setUserInfo({
                ...userInfo.userInfo,
                hasNotifications,
            });
        });
    }, [refreshKey]);

    useEffect(() => {
        onDeleteModalOpenChange?.(showDeleteModal);

        return () => {
            onDeleteModalOpenChange?.(false);
        };
    }, [onDeleteModalOpenChange, showDeleteModal]);

    const showOnlyErrorMessage = !isNotificationsLoading && Boolean(error);
    const hasItems = notifications.length > 0;
    const showEmptyState = !isNotificationsLoading && !error && !hasItems;

    return (
        <div id={dropdownId} className="alerts-dropdown__content">
            <ModalGlobal
                show={showDeleteModal}
                onClose={closeDeleteModal}
                onCancel={closeDeleteModal}
                onSave={handleDeleteNotification}
                title={t("notification:modalDeleteNotificationTitle")}
                contentType="deleteWithoutInput"
                subtitle={t("notification:modalDeleteNotificationSubtitle")}
                subtitle2={""}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                isLoading={deleteLoading}
                handleMotiveDeleteChange={() => undefined}
            />

            <div className="notifications-panel__body">
                {messageName && (
                    <AlertMessage
                        key={messageKey}
                        message={messageName}
                        type="success"
                        displayTime={3000}
                    />
                )}

                {isNotificationsLoading && (
                    <div className="overlay-spinner">
                        <Spinner animation="border" variant="light" />
                    </div>
                )}

                {!isNotificationsLoading && error && (
                    <AlertMessage
                        message={t("notification:alertMessageError")}
                        type="danger"
                        displayTime={3000}
                    />
                )}

                {!showOnlyErrorMessage && (
                    <>
                        {showEmptyState && (
                            <div className="notifications-panel__empty text-center text-muted py-4">
                                No hay notificaciones.
                            </div>
                        )}
                        {hasItems && (
                            <div className="d-flex flex-column gap-2 dropdown-notifications-list">
                                {notifications.map((notification) => {
                                    const createdAt = notification.dataCreacio
                                        ? formatTimestampToDDMMYYYYHHMM(new Date(notification.dataCreacio).getTime())
                                        : "";
                                    const ContextIcon = notification.warnTypeDestination === "UAC" ? Users : User;
                                    const StatusIcon = notification.isRead ? MailOpen : Mail;

                                    return (
                                        <Card
                                            key={notification.id}
                                            className={`notification-card ${notification.isRead ? "notification-card--read" : "notification-card--new"}`}
                                        >
                                            <Card.Body className="notification-card__body">
                                                <div className="notification-card__header">
                                                    <div className="notification-card__header-main">
                                                        <div className="notification-card__context-icon">
                                                            <ContextIcon size={20} strokeWidth={1.8} />
                                                        </div>
                                                    </div>

                                                    <div className="notification-card__actions">
                                                        <button
                                                            type="button"
                                                            title="Delete notification"
                                                            className="notification-card__action-button notification-card__action-button--danger"
                                                            aria-label={t("notification:tooltipButtonNotification")}
                                                            onClick={() => openDeleteModal(notification)}
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Mark notification as read"
                                                            className="notification-card__action-button"
                                                            aria-label={notification.isRead ? t("notification:tooltipButtonNotReadNotification") : t("notification:tooltipButtonReadNotification")}
                                                            onClick={() => handleIsReadToggle(notification)}
                                                        >
                                                            <StatusIcon size={18} strokeWidth={1.8} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="notification-card__content">
                                                    <div className="notification-card__message">
                                                        {notification.warnText || ""}
                                                    </div>
                                                    <div className="notification-card__meta">
                                                        {createdAt}
                                                        {createdAt && notification.userCreatorName ? " - " : ""}
                                                        {notification.userCreatorName ? `(${notification.userCreatorName})` : ""}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {hasItems && !showOnlyErrorMessage && (
                <div className={`notifications-panel__footer${showMarkAllAsReadButtons ? " notifications-panel__footer--expanded" : ""}`}>
                    {!showMarkAllAsReadButtons ? (
                        <div className="notifications-panel__footer-row">
                            <Form.Check
                                className="notifications-panel__switch"
                                type="switch"
                                id="custom-switch"
                                label={t("common:buttons.togglePersonalNotificationsRecords")}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOnlyPersonals(e.target.checked)}
                                checked={onlyPersonals}
                            />
                            <Button
                                variant="primary"
                                onClick={() => setShowMarkAllAsReadButtons(true)}
                                disabled={isMarkAsReadLoading}
                            >
                                {isMarkAsReadLoading && (
                                    <Spinner animation="border" variant="light" style={{width: "16px", height: "16px", fontSize: "10px", marginRight: "5px"}} />
                                )}
                                {t("notification:markAllAsRead")}
                            </Button>
                        </div>
                    ) : (
                        <div className="notifications-panel__footer-confirm">
                            <div className="notifications-panel__footer-text">
                                {t("notification:markAllAsReadModalText")}
                            </div>
                            <div className="notifications-panel__footer-actions">
                                <Button variant="primary" size="sm" onClick={() => markAllAsRead(false)}>
                                    {t("notification:yesAll")}
                                </Button>
                                <Button variant="primary" size="sm" onClick={() => markAllAsRead(true)}>
                                    {t("notification:yesOnlyPersonals")}
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => setShowMarkAllAsReadButtons(false)}>
                                    {t("common:no")}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}